import { Modal, Group, Text, ScrollArea, Center, Loader, Timeline, Badge, Box } from '@mantine/core'
import { IconHistory } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'

export interface HistoryEvent {
  id: number
  type: string
  performedBy?: string
  note?: string
  noteKey?: string
  noteParams?: string // JSON string จาก backend — parse ก่อนส่งเข้า t()
  createdAt: string
}

export interface HistoryTypeConfig {
  color: string
  label: string
  icon: string
}

/**
 * Modal ประวัติแบบ Timeline ที่ใช้ร่วมกันระหว่าง Handover/Transfer/Borrow
 * ลอกแพทเทิร์นจาก Asset History ที่มีอยู่แล้วใน AssetsPage.tsx ให้หน้าตาสอดคล้องกันทั้งระบบ
 * แต่ละหน้าส่ง typeConfig ของตัวเองเข้ามา (ไอคอน/สี/label ต่างกันตาม MovementType ของแต่ละโมดูล)
 */
export default function HistoryTimelineModal({
  opened, onClose, title, data, loading, typeConfig,
}: {
  opened: boolean
  onClose: () => void
  title: string
  data: HistoryEvent[]
  loading: boolean
  typeConfig: Record<string, HistoryTypeConfig>
}) {
  const { t, i18n } = useTranslation()
  return (
    <Modal opened={opened} onClose={onClose} title={
      <Group gap="xs">
        <IconHistory size={20} />
        <Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 18 }}>{title}</Text>
      </Group>
    } size="lg" radius="lg">
      <ScrollArea h={480}>
        {loading ? (
          <Center p="xl"><Loader color="teal" /></Center>
        ) : data.length === 0 ? (
          <Center p="xl"><Text c="dimmed">{t('common.noHistory', 'ไม่มีประวัติ')}</Text></Center>
        ) : (
          <Timeline active={data.length} bulletSize={28} lineWidth={2} mt="sm">
            {data.map((h) => {
              const cfg = typeConfig[h.type] ?? { color: 'gray', label: h.type, icon: '•' }
              // ── note ที่แปลได้จริงตามภาษาที่เลือกอยู่ — ใช้ noteKey/noteParams ถ้ามี (เหตุการณ์ใหม่
              // หลังแก้ i18n รอบนี้) ไม่งั้น fallback ไปที่ note ดิบที่ backend เขียนไว้ตอนบันทึก
              // (เหตุการณ์เก่าก่อนมี noteKey — ยังคงเป็นภาษาที่ถูก bake ไว้ตอนนั้น แปลย้อนหลังไม่ได้)
              let displayNote = h.note
              if (h.noteKey) {
                try {
                  const params = h.noteParams ? JSON.parse(h.noteParams) : {}
                  displayNote = String(t(h.noteKey, h.note ?? '', params))
                } catch { /* JSON พังหรือคีย์ไม่มีจริง — ใช้ note ดิบแทน */ }
              }
              return (
                <Timeline.Item
                  key={h.id}
                  bullet={<Text size="xs">{cfg.icon}</Text>}
                  title={
                    <Group gap={6}>
                      <Badge color={cfg.color} variant="light" size="sm">{cfg.label}</Badge>
                      <Text size="xs" c="dimmed">
                        {new Date(h.createdAt).toLocaleString(i18n.language === 'th' ? 'th-TH' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </Text>
                    </Group>
                  }
                >
                  <Box mt={4}>
                    {h.performedBy && <Text size="xs" c="dimmed">{t('common.by', 'By')}: <b>{h.performedBy}</b></Text>}
                    {displayNote && <Text size="xs" c="dimmed" mt={2}>{displayNote}</Text>}
                  </Box>
                </Timeline.Item>
              )
            })}
          </Timeline>
        )}
      </ScrollArea>
    </Modal>
  )
}
